// 出发参数规划（EP-005 / GDD 02 §②-2.2）。
// 由系统抽目的地、决定长旅行、固化时长。timeScale 只影响新出发（除以倍速后写入，GDD 02 §②-2.2-5）。
// 纯函数，rng 注入；数值来自 dropConfig.destinationWeights 与下方常量（非硬编码散落）。
import { weightedPick } from './weightedPick';
import type { DestinationId, SaveDataV1 } from '../save/types';
import { dropConfig } from '../domain/drop/dropConfig';

export interface DeparturePlan {
  destinationId: DestinationId;
  travelDurationMs: number;
  isLongTrip: boolean;
}

export interface DepartureOptions {
  /**
   * 本地开发（localhost + import.meta.env.DEV）快测开关：把时长等比缩短，便于手测不用干等。
   * 生产构建下该值恒为 false（见 isLocalDevRuntime），分支被静态消除，绝不生效。
   */
  localDevShorten?: boolean;
}

// 首旅（未 isFirstTripDone）：固定 10 分钟。教学回路 —— 让玩家第一次会话内就看到完整回归
// （GDD 02 §5.1），**不参与**下面的随机区间。
const FIRST_TRIP_MS = 10 * 60_000;

// ────────────────────────────────────────────────────────────┐
// 🆕 P6-POLISH-ENG-002（07 §A.2）四档分段加权分布：30 分钟 ~ 6 小时
// ────────────────────────────────────────────────────────────┘
// 为什么分段加权而不是均匀分布：均匀分布下近半数旅行要等 3 小时以上（期望 195min），
// 玩家清醒期最多收 3–4 次反馈，回访动机衰减；但长尾又必须保留（旅行青蛙式期待感来自
// "不知道它什么时候回来"）。四档加权把 ≤2h 的占比拉到 60%，同时留 15% 的"远行"制造悬念。
//
// 期望 130.5min ≈ 2 小时 10 分；中位数 ≈100min；P(≤120min) = 60%。
// 档权重刻意不用浮点比例而是直接用权重值喂给 weightedPick（避免另写归一化逻辑）。
const DURATION_BANDS: readonly { minMs: number; maxMs: number; weight: number }[] = [
  { minMs: 30 * 60_000, maxMs: 60 * 60_000, weight: 30 }, // 短：在家门口晃一圈
  { minMs: 60 * 60_000, maxMs: 120 * 60_000, weight: 30 }, // 中：走远一点
  { minMs: 120 * 60_000, maxMs: 240 * 60_000, weight: 25 }, // 长：去了较远的地方
  { minMs: 240 * 60_000, maxMs: 360 * 60_000, weight: 15 }, // 远：可能要等半天
];

// 由时长派生的长旅行阈值（07 §A.5）：≥ 4 小时才算远行 → 恰好覆盖"远"档整档 = 15%。
// 选 4h 而不是 3h：3h 会让长旅行占比到 27.5%，而长旅行留言池只有 2 条，会刷屏。
const LONG_TRIP_THRESHOLD_MS = 240 * 60_000;

// —— 本地开发快测时长（P-DEV-TRIP-001，2026-09 改造为区间映射）——
// 需求：老板在 localhost 上手测时不想等几十分钟到几小时。
//
// ⚠️ 原来是等比缩放（LOCAL_DEV_SCALE = 1/30）+ clamp 到 [20s, 180s]，新分布上界升到 360min 后
// **会失效**：90min 起就全被 clamp 成同一个 180s，短途/中程/远行在本地完全没区别，
// 老板测不出档位差异。故改为区间线性映射（07 §A.6 方案 A2）。
//
// 名义 [30min, 360min] → 本地 [60s, 180s]，之后再 clamp 到 [20s, 180s] 兜底。
export const NOMINAL_MIN_MS = 30 * 60_000;
export const NOMINAL_MAX_MS = 360 * 60_000;
export const LOCAL_MAP_MIN_MS = 60 * 1_000; // 本地映射下界
export const LOCAL_MAP_MAX_MS = 3 * 60_000; // 本地映射上界（沿用原 LOCAL_DEV_MAX_MS）
const LOCAL_DEV_MIN_MS = 20 * 1_000; // 兜底下限
const LOCAL_DEV_MAX_MS = 3 * 60_000; // 兜底上限：任何一档本地都 ≤ 3 分钟

// 本地回环 hostname（小写比较）。生产域名（S3 / CloudFront / 自定义域）均不在其中。
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1']);

/** 判定 hostname 是否属本地回环（纯函数，可单测；不含 DEV 判断）。 */
export function isLocalDevHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return LOCAL_HOSTNAMES.has(h) || h.endsWith('.localhost');
}

/**
 * 「是否启用本地快测时长」的唯一判定式：Vite DEV 模式 **且** hostname 属本地回环。
 * 两个条件同时满足才缩短 —— 生产构建 import.meta.env.DEV 静态替换为 false，
 * 即使有人把线上域名改指 localhost 也不会缩短。
 */
export function isLocalDevEnabled(dev: boolean, hostname: string): boolean {
  return dev && isLocalDevHost(hostname);
}

/** 运行时门控：无 window（SSR / node 测试）一律 false。 */
export function isLocalDevRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  return isLocalDevEnabled(import.meta.env.DEV, window.location.hostname);
}

export function planDeparture(
  save: SaveDataV1 | null,
  rng: () => number,
  timeScale = 1,
  options: DepartureOptions = {},
): DeparturePlan {
  // rng 消耗顺序（**共 3 次，改造前后不变**，既有脚本化 rng 测试序列结构仍可用）：
  //   ① weightedPick(destinationWeights) → ② weightedPick(DURATION_BANDS) → ③ 档内均匀
  //   首旅只消耗 ①（时长固定 10min，不再多抽）。
  //   ⚠️ isLongTrip 已从"再抽一次 rng"改为由③的结果派生 —— 若改回独立一抽，这里会变成 4 次。
  const destinationId = weightedPick(
    Object.keys(dropConfig.destinationWeights) as DestinationId[],
    Object.values(dropConfig.destinationWeights),
    rng,
  );

  let durationMs: number;
  if (!save || !save.stats.isFirstTripDone) {
    // 首旅固定 10 分钟，**不消耗**第二个 rng（题型见下方"rng 消耗顺序"注释）。
    durationMs = FIRST_TRIP_MS;
  } else {
    // ① 抽档（weightedPick）
    const bandIdx = weightedPick(
      DURATION_BANDS.map((_, i) => i),
      DURATION_BANDS.map((b) => b.weight),
      rng,
    );
    const band = DURATION_BANDS[bandIdx] ?? DURATION_BANDS[0]!;
    // ② 档内均匀
    durationMs = band.minMs + rng() * (band.maxMs - band.minMs);
  }

  // 🆕 本地快测：名义区间**线性映射**到本地区间，再 clamp 兜底，最后才交给 timeScale。
  //   顺序不可改（与 P-DEV-TRIP-001 一致）：先本地缩放 → 再 ÷timeScale。
  //   07 §A.6 映射表：30min→60s / 120min→92.7s / 360min→180s / 首旅 10min→≈52.7s（低于下界但仍在兜底区间内）。
  if (options.localDevShorten === true) {
    const ratio =
      (durationMs - NOMINAL_MIN_MS) / (NOMINAL_MAX_MS - NOMINAL_MIN_MS);
    const mapped = LOCAL_MAP_MIN_MS + ratio * (LOCAL_MAP_MAX_MS - LOCAL_MAP_MIN_MS);
    durationMs = Math.min(Math.max(Math.round(mapped), LOCAL_DEV_MIN_MS), LOCAL_DEV_MAX_MS);
  }

  const scaled = timeScale > 0 ? durationMs / timeScale : durationMs;
  // 🆕 isLongTrip **由时长派生**（07 §A.5），不再是独立的一抽。
  const isLongTrip = durationMs >= LONG_TRIP_THRESHOLD_MS;
  return { destinationId, travelDurationMs: Math.round(scaled), isLongTrip };
}
